import React, { useContext, useState } from 'react';
import { Alert, Box, Button, Header, Link, SpaceBetween, StatusIndicator } from '@amzn/awsui-components-react';
import { TableProps } from '@amzn/awsui-components-react/polaris/table';
import TPPolarisTable from 'src/components/shared/TPPolarisTable';
import { MultidimensionalAllocationContext } from 'src/services/multidimensional-allocation/MultidimensionalAllocationContext';
import { CommonBalancePool } from 'src/models/multidimensional-allocation/CommonBalancePool';
import { WorksheetStatus } from 'src/models/multidimensional-allocation/MultiAllocationSheetData';
import {
    STATUS_INDICATOR_CONFIG,
    PAGE_SIZE_OPTIONS,
    CBP_COLUMN_DEFINITIONS,
    CBP_VISIBLE_CONTENT_OPTIONS,
    CBP_DEFAULT_COLLECTION_PREFERENCES,
    CBP_FILTERING_PROPERTIES,
} from 'src/components/multidimensional-allocation/common/Constants';
import CreateCommonBalancePoolModal from './CreateCommonBalancePoolModal';
import { getPermissions } from 'src/components/AppPermissions';
import { useHistory } from 'react-router-dom';
import CONSTANTS from 'src/utils/constants';
import { AppModules } from 'src/models/permissions/RolePermissions';
import DateUtils from 'src/utils/dateUtils';

/**
 * Common Balance Pools tab (MDA 1.1 Phase 2).
 *
 * Pool list + Step 1 create (name + grain). Data is sourced through `CommonBalancePoolService`,
 * which calls the CBP WebAPI (contract in `ICTransferPricingWebAPILambdaModel`). Step 2 (inputs +
 * calculated columns) and the validation panel land in later CRs.
 */
export default function CommonBalancePools() {
    const { services } = useContext(MultidimensionalAllocationContext);
    const { commonBalancePoolService } = services;
    const { canEdit } = getPermissions(AppModules.MULTIDIMENSIONAL_ALLOCATION);

    const history = useHistory();
    const [createVisible, setCreateVisible] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    // Keyed hook: bumping refreshKey (e.g. after a successful create) re-fetches the list.
    const [pools, loading, error] = commonBalancePoolService.listCommonBalancePools(`fetch-${refreshKey}`);

    // Column metadata (id/header/sortingField) is centralized in common/Constants; only the cell
    // renderers — which close over `history`, status/date helpers — stay here.
    const columnDefinitions: TableProps.ColumnDefinition<CommonBalancePool>[] = [
        {
            ...CBP_COLUMN_DEFINITIONS.NAME,
            cell: (item) => (
                <Link onFollow={() => history.push(`${CONSTANTS.PAGE_NAV.COMMON_BALANCE_POOL_BUILDER.URL}/${item.poolId}`)}>
                    {item.poolName ?? item.poolId}
                </Link>
            ),
            isRowHeader: true,
        },
        { ...CBP_COLUMN_DEFINITIONS.STATUS, cell: (item) => (item.status ? getStatusIndicator(item.status) : '-') },
        { ...CBP_COLUMN_DEFINITIONS.GRAIN, cell: (item) => item.grain },
        { ...CBP_COLUMN_DEFINITIONS.CREATED_BY, cell: (item) => item.createdBy ?? '-' },
        {
            ...CBP_COLUMN_DEFINITIONS.LAST_UPDATED_AT,
            cell: (item) => (item.lastUpdatedAt != null ? DateUtils.formatDate(item.lastUpdatedAt, 'MMM D, YYYY') : '-'),
        },
    ];

    // The error itself is surfaced by the banner above the table (independent of item count), so the
    // empty cell just points there — the "no pools" prompt is only for the genuinely-empty case.
    const tableEmptyState = error ? (
        <Box textAlign="center" color="text-status-error">
            Could not load common balance pools — see the message above.
        </Box>
    ) : (
        <Box textAlign="center">
            <SpaceBetween direction="vertical" size="s">
                <b>No common balance pools found</b>
                {canEdit && 'Get started by creating your first common balance pool.'}
                {canEdit && (
                    <Button variant="primary" onClick={() => setCreateVisible(true)}>
                        Create common balance pool
                    </Button>
                )}
            </SpaceBetween>
        </Box>
    );

    const header = (
        <Header
            counter={pools?.pools ? `(${pools.pools.length})` : undefined}
            actions={
                canEdit ? (
                    <Button variant="primary" onClick={() => setCreateVisible(true)}>
                        Create common balance pool
                    </Button>
                ) : undefined
            }
        >
            Common Balance Pools
        </Header>
    );

    return (
        <>
            <CreateCommonBalancePoolModal
                visible={createVisible}
                onHide={() => setCreateVisible(false)}
                onCreated={() => setRefreshKey((key) => key + 1)}
            />
            <SpaceBetween direction="vertical" size="m">
                {error && (
                    <Alert
                        type="error"
                        header="Unable to load common balance pools"
                        action={<Button onClick={() => setRefreshKey((key) => key + 1)}>Retry</Button>}
                    >
                        {error}
                    </Alert>
                )}
                <TPPolarisTable
                    header={header}
                    columnDefinitions={columnDefinitions}
                    tableItems={pools?.pools || []}
                    isLoading={loading}
                    tableEmptyState={tableEmptyState}
                    resizableColumns={true}
                    sortingColumn="poolName"
                    trackBy="poolId"
                    filteringProperties={CBP_FILTERING_PROPERTIES}
                    filteringEmptyText="No common balance pools match the filter criteria"
                    filteringLoadingText="Loading common balance pools..."
                    preferencesStorageKey="cbp-pool-table-preferences"
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    visibleContentOptions={CBP_VISIBLE_CONTENT_OPTIONS}
                    defaultPreferences={CBP_DEFAULT_COLLECTION_PREFERENCES}
                />
            </SpaceBetween>
        </>
    );
}

function getStatusIndicator(status: string) {
    // Fall back gracefully if the backend returns a status not in the config, rather than throwing
    // on the array destructure and crashing the whole list render.
    const [label, statusType, color] = STATUS_INDICATOR_CONFIG[status as WorksheetStatus]
        ?? ([status, 'info', undefined] as unknown as (typeof STATUS_INDICATOR_CONFIG)[WorksheetStatus]);
    return (
        <StatusIndicator type={statusType} colorOverride={color}>
            {label}
        </StatusIndicator>
    );
}
